/** What the campaign screens share (plan E06-T05): status labels and the dry-run summary. */
import type { Schemas } from "../api/client";
import { useFormat, useT, type MessageId } from "../i18n";

export type DryRun = Schemas["DryRunOut"];
export const CAMPAIGN_TYPES = ["promotional", "service", "service_regulated", "transactional", "transactional_regulated", "debt_collection"];
const FINISHED = ["done", "failed", "blocked", "suppressed"];

export function CampaignStatus({ status }: { status: string }) {
  const t = useT();
  const tone = status === "running" ? "border-[var(--color-success)]" : status === "paused" ? "border-[var(--color-warning)]"
    : "border-[var(--color-border)]";
  return <span className={`rounded-full border px-2 py-0.5 text-xs ${tone}`}>{t(`campaigns.status.${status}` as MessageId)}</span>;
}

/** How many of the contacts are finished (done, failed, blocked, suppressed), of all. */
export function finished(contacts: Record<string, number>): { done: number; total: number } {
  const total = Object.values(contacts).reduce((a, b) => a + b, 0);
  const done = FINISHED.reduce((a, s) => a + (contacts[s] ?? 0), 0);
  return { done, total };
}

export function Progress({ contacts }: { contacts: Record<string, number> }) {
  const t = useT();
  const { done, total } = finished(contacts);
  return (
    <div className="space-y-1">
      <progress className="h-2 w-full" max={Math.max(total, 1)} value={done} aria-label={t("campaigns.progress")} />
      <p className="text-sm text-[var(--color-muted)]">{t("campaigns.progressText", { done, total })}</p>
    </div>
  );
}

/** "1,240 allowed now · 310 deferred to tomorrow 09:00 · 12 blocked: do-not-call" (design 11 §3.4). */
export function DryRunSummary({ run }: { run: DryRun }) {
  const t = useT();
  const format = useFormat();
  const rules = (by: Record<string, number>) =>
    Object.entries(by).sort((a, b) => b[1] - a[1]).map(([rule, n]) => t("campaigns.dry.rule", { rule, n })).join(", ");
  return (
    <section aria-labelledby="dry-run" className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
      <h3 id="dry-run" className="font-semibold">{t("campaigns.dry.title", { when: format.dateTime(run.at) })}</h3>
      <ul className="space-y-1 text-sm">
        <li>{t("campaigns.dry.allowed", { n: run.allowed, total: run.contacts })}</li>
        {run.deferred.count > 0 && (
          <li>
            {t("campaigns.dry.deferred", { n: run.deferred.count, when: run.deferred.from ? format.dateTime(run.deferred.from) : "—" })}
            {" "}<span className="text-[var(--color-muted)]">({rules(run.deferred.by_rule)})</span>
          </li>
        )}
        {run.blocked.count > 0 && (
          <li className="text-[var(--color-danger)]">{t("campaigns.dry.blocked", { n: run.blocked.count, rules: rules(run.blocked.by_rule) })}</li>
        )}
        {Object.keys(run.warnings).length > 0 && <li>{t("campaigns.dry.warnings", { rules: rules(run.warnings) })}</li>}
      </ul>
      {(["blocked", "deferred"] as const).map((kind) => (run.examples[kind] ?? []).length > 0 && (
        <details key={kind} className="text-sm">
          <summary className="cursor-pointer text-[var(--color-primary)]">{t(`campaigns.dry.examples.${kind}`)}</summary>
          <ul className="mt-1 space-y-1">
            {(run.examples[kind] ?? []).map((e) => (
              <li key={e.e164}>
                <span className="font-mono">{e.e164}</span>: {e.reasons.map((r) => `${r.rule}: ${r.detail}`).join("; ")}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </section>
  );
}
